



































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import SearchAndAddBox, { FilterOption } from '@/components/base/SearchAndAddBox.vue';
import { ReportsState, ReportsAction, ReportsActionType } from '@/store/modules/reports/types';
import { AppGetter } from '@/store/modules/app/types';
import { getLoggedUserId } from '@/utils/helpers';

const reports = namespace('reports');

@Component({
  components: {
    SearchAndAddBox
  }
})
export default class ReportsTab extends Vue {
  @Prop() reportsStatus!: ReportsState['reportsStatus'];

  collapsedStates: { [key: string]: boolean } = {};

  currentUserId: string = 'none';

  dialog = false;

  reportToDelete: string | null = null;

  @reports.Action(ReportsAction.DownloadReportPdf)
  downloadReportPdf!: ReportsActionType['DOWNLOAD_REPORT_PDF'];

  @reports.Action(ReportsAction.DownloadReportXls)
  downloadReportXls!: ReportsActionType['DOWNLOAD_REPORT_XLS'];

  @reports.Action(ReportsAction.DeleteReportConfig)
  deleteReportConfig!: ReportsActionType['DELETE_REPORT_CONFIG'];

  get filterOptions(): FilterOption[] {
    return [
      {
        name: 'type',
        selectOptions: [
          { text: '-', value: 'all' },
          { text: this.$t('filters.single') as string, value: 'single' },
          { text: this.$t('filters.scheduled') as string, value: 'scheduled' }
        ]
      },
      {
        name: 'filter_type',
        selectOptions: [
          { text: '-', value: 'all' },
          { text: this.$t('reports.filterAll') as string, value: 'none' },
          { text: this.$t('reports.filterCircuit') as string, value: 'circuit_uuid' },
          { text: this.$t('reports.filterGroup') as string, value: 'group_uuid' },
          { text: this.$t('reports.filterGateway') as string, value: 'object_id' }
        ]
      }
    ];
  }

  get singleReportConfigKeys(): string[] {
    return [
      'filter_type',
      'filter_value',
      'generation_date'
    ];
  }

  get scheduledReportConfigKeys(): string[] {
    return [
      'filter_type',
      'filter_value',
      'start_date',
      'end_date',
      'repeating_interval',
      'last_generation_date'
    ];
  }

  getDisplayedConfig(report: any, reportType: string) {
    if (!report.config) return {};

    const keys = reportType === this.$t('filters.single') ? this.singleReportConfigKeys : this.scheduledReportConfigKeys;

    const filteredConfig = keys.reduce((acc, key) => {
      if (key === 'generation_date') {
        acc[key] = report.generation_date;
      } else if (key in report.config) {
        acc[key] = report.config[key];
      }
      return acc;
    }, {} as any);

    return filteredConfig;
  }

  get allReports() {
    const singleReports = (this.reportsStatus?.reports || [])
      .map((report) => {
        const config = this.getConfigByUuid(report.config_uuid);
        return {
          id: `single-${report.id}`,
          title: config?.object_display_name,
          config,
          generation_date: report.createdAt,
          link_pdf: this.$t('reports.downloadPdf') as string,
          link_csv: this.$t('reports.downloadXls') as string,
          reportId: report.id,
          type: this.$t('filters.single') as string,
          filterType: config?.filter_type || 'none'
        };
      })
      .sort((a, b) => (b.reportId || 0) - (a.reportId || 0));

    const pendingSingleConfigs = (this.reportsStatus?.configs || [])
      .filter((config) => config.type === 'single' && config.created_by === this.currentUserId)
      .filter((config) => !this.reportsStatus?.reports?.some((r) => r.config_uuid === config.uuid))
      .map((config) => ({
        id: `pending-single-${config.uuid}`,
        title: config.object_display_name,
        config,
        generation_date: config.createdAt,
        link_pdf: false, // Disabled until report exists
        link_csv: false,
        reportId: null,
        type: this.$t('filters.single') as string,
        filterType: config.filter_type
      }));

    const scheduledReports = (this.reportsStatus?.configs.filter((config) => config.type === 'scheduled') || []).map((config) => ({
      id: `scheduled-${config.uuid}`,
      title: config.object_display_name,
      config,
      frequency: config.repeating_interval,
      status: config.enabled ? this.$t('reports.active') as string : this.$t('reports.inactive') as string,
      type: this.$t('filters.scheduled') as string,
      filterType: config.filter_type
    }));

    return [...pendingSingleConfigs, ...singleReports, ...scheduledReports];
  }

  filteredReports(searchPhrase: string, computedFilterParams: any[]) {
    const typeFilter = computedFilterParams.find((p) => p.name === 'type')?.value || 'all';
    const filterTypeFilter = computedFilterParams.find((p) => p.name === 'filter_type')?.value || 'all';

    return this.allReports.filter((report) => {
      // Search filter - concatenate title with filter_value (filterType)
      const searchString = `${report.title} ${report.filterType}`.toLowerCase();
      const matchesSearch = !searchPhrase || searchString.includes(searchPhrase.toLowerCase());

      // Type filter
      const matchesType = typeFilter === 'all'
      || (typeFilter === 'single' && report.type === this.$t('filters.single'))
      || (typeFilter === 'scheduled' && report.type === this.$t('filters.scheduled'));

      // Filter type filter
      const matchesFilterType = filterTypeFilter === 'all' || report.filterType === filterTypeFilter;

      // User filter
      const createdByCurrentUser = report.config?.created_by === this.currentUserId;

      return matchesSearch && matchesType && matchesFilterType && createdByCurrentUser;
    });
  }

  getConfigByUuid(uuid: string) {
    return this.reportsStatus?.configs.find((config) => config.uuid === uuid);
  }

  formatReportParameters(configUuid: string): string {
    const config = this.getConfigByUuid(configUuid);
    if (!config) return 'Unknown configuration';

    return `Filter: ${config.filter_type}, Start: ${String(config.start_date).split('T')[0]}, End: ${String(config.end_date).split('T')[0]}`;
  }

  async downloadPdf(reportId: number) {
    await this.downloadReportPdf(reportId);
  }

  async downloadXls(reportId: number) {
    await this.downloadReportXls(reportId);
  }

  formatParameterKey(key: string): string {
    // Convert snake_case to Title Case
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatParameterValue(key: string, value: any): string {
    if (value === null || value === undefined) {
      return 'Not set';
    }

    // Format dates
    if ((key === 'start_date' || key === 'end_date' || key === 'last_generation_date') && value) {
      return new Date(value).toLocaleDateString('pl-PL');
    }

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  }

  async deleteReport(uuid: string) {
    await this.deleteReportConfig(uuid);
  }

  isReportCollapsed(reportId: string): boolean {
    return this.collapsedStates[reportId] ?? true;
  }

  toggleReportDetails(reportId: string): void {
    this.$set(this.collapsedStates, reportId, !this.isReportCollapsed(reportId));
  }

  handleDeleteClick(report: any) {
    const uuid = report.config?.uuid;
    if (uuid) this.modalAction(uuid);
  }

  modalAction(reportUuid: string) {
    this.reportToDelete = reportUuid;
    this.dialog = true;
  }

  closeModal() {
    this.reportToDelete = null;
    this.dialog = false;
  }

  deleteModal() {
    this.deleteReport(this.reportToDelete!);
    this.dialog = false;
    this.reportToDelete = null;
  }

  mounted() {
    const token = this.$store.getters[`app/${AppGetter.GetToken}`];
    const userId = getLoggedUserId(token);
    if (userId) this.currentUserId = userId;
  }
}
